import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { message } from 'antd'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import CircularLoading from '../../../../../components/CircularProgress'
import InputUpload from '../../../../../components/InputUpload'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { EOrderType, IOrder } from '../../../../../types/order'

interface IOrderEditPaymentFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  order?: IOrder
  onSuccess: () => void
}

interface IPaymentFormModal {
  paymentMethod: EOrderType
  orderDate: string | Date
  paymentDate: string | Date
  paymentUrl?: string
  note?: string
}

const getPaymentFormValidationSchema = () => {
  return Yup.object({
    paymentMethod: Yup.string().required('กรุณาเลือกวิธีการชำระเงิน'),
    orderDate: Yup.string().required('กรุณาระบุวันที่สั่งซื้อ'),
    paymentDate: Yup.string(),
    paymentUrl: Yup.string(),
    note: Yup.string(),
  })
}

export const OrderEditPaymentFormModal = (
  props: IOrderEditPaymentFormModalProps,
) => {
  const { isOpen, onClose, order, onSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true)
    formik.setValues({
      paymentMethod: order?.paymentMethod ?? EOrderType.COD,
      orderDate: order?.orderDate ?? '',
      paymentDate: order?.paymentDate ?? '',
      paymentUrl: order?.paymentUrl ?? '',
      note: order?.note ?? '',
    })
    setIsLoading(false)
  }, [isOpen, order])

  const formik = useFormik<IPaymentFormModal>({
    initialValues: {
      paymentMethod: EOrderType.COD,
      orderDate: '',
      paymentDate: '',
      paymentUrl: '',
      note: '',
    } as IPaymentFormModal,
    validationSchema: getPaymentFormValidationSchema(),
    onSubmit: async (values: IPaymentFormModal) => {
      setIsLoading(true)
      try {
        await axiosInstance.put(`/order/${order?.objectId}/payment`, {
          paymentMethod: values.paymentMethod,
          orderDate: values.orderDate,
          paymentDate: values.paymentDate,
          paymentUrl: values.paymentUrl,
          note: values.note,
        })
        message.success(`แก้ไขการชำระเงิน สำเร็จ`)
        formik.resetForm()
        onClose(false)
        onSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      await formik.setFieldValue('paymentUrl', response.data.url)
      return message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="xl">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>แก้ไขการชำระเงิน</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={6}>
              <Select
                id="paymentMethod"
                name="paymentMethod"
                placeholder="การชำระเงิน"
                value={formik.values.paymentMethod}
                onChange={formik.handleChange}
                fullWidth
              >
                <MenuItem value={EOrderType.COD}>เก็บเงินปลายทาง</MenuItem>
                <MenuItem value={EOrderType.BANK}>ชำระเงินผ่านธนาคาร</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format={ONLY_DATE_FORMAT}
                  //disableFuture
                  label="วันที่สั่งซื้อ"
                  name="orderDate"
                  value={dayjs(formik.values.orderDate) ?? dayjs()}
                  onChange={(e) => {
                    formik.setFieldValue('orderDate', e?.format())
                  }}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      helperText:
                        formik.touched.orderDate &&
                        (formik.errors.orderDate as string),
                      error: !!(
                        formik.touched.orderDate && formik.errors.orderDate
                      ),
                      onBlur: formik.handleBlur,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {formik.values.paymentMethod === EOrderType.BANK && (
              <>
                <Grid item xs={12} lg={6}>
                  <InputUpload
                    onRemove={() => formik.setFieldValue('paymentUrl', '')}
                    handleUploadClick={handleUploadClick}
                    imageUrl={formik.values.paymentUrl ?? ''}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format={ONLY_DATE_FORMAT}
                      // disableFuture
                      label="วันที่ชำระ"
                      name="paymentDate"
                      value={dayjs(formik.values.paymentDate) ?? dayjs()}
                      onChange={(e) => {
                        formik.setFieldValue('paymentDate', e?.format())
                      }}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          helperText:
                            formik.touched.paymentDate &&
                            (formik.errors.paymentDate as string),
                          error: !!(
                            formik.touched.paymentDate &&
                            formik.errors.paymentDate
                          ),
                          onBlur: formik.handleBlur,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
            <Grid item xs={12} lg={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={`หมายเหตุ`}
                name="note"
                helperText={
                  formik.touched.note && (formik.errors.note as string)
                }
                error={!!(formik.touched.note && formik.errors.note)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                disabled={formik.isSubmitting}
                startIcon={
                  formik.isSubmitting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SaveIcon />
                  )
                }
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                แก้ไขการชำระเงิน
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
